/**
 * `router/feature.js`
 *  - featureページ用スクリプト
 *
 * 使用方法:
 *  - /router/index.js initRouterで動的読み込み
 *  - import {featureRouter} from "./router/feature.js";
 */

import { getContainer } from "../utils/index.js";


export const featureRouter = async (data) => {
  console.log("📖 Initializing feature page...");

  // KVグラデーションシーン (home_about サイズ感)
  const { initFeatureKvGradient } = await import(
    /* webpackChunkName: "three-kv-gradient" */
    '../modules/three/threeKvGradient.js'
  );
  initFeatureKvGradient(data, '#canvas_kv');

  // ─── 紙芝居形式 (画像 pin + フェード切替) ───
  // 参考: nex/scrollSection.js pinFigureContents
  //  - すべての .feature_section_meta を viewport center に pin
  //    (各 meta の center が viewport center に到達した瞬間 pin 開始、
  //     wrapper .feature_sections の bottom が viewport bottom に達するまで保持)
  //  - 同位置に複数 meta が積まれるが opacity 制御で active な一つだけ表示
  //  - 各 section の top/bottom が viewport center を跨ぐ瞬間に
  //    onEnter/onLeave で隣接 meta がクロスフェード
  const [{ gsap }, ScrollTriggerMod] = await Promise.all([
    import(/* webpackChunkName: "gsap" */ '../modules/gsap/gsap.js'),
    import(/* webpackChunkName: "gsap" */ 'gsap/ScrollTrigger'),
  ]);
  const { ScrollTrigger } = ScrollTriggerMod;
  gsap.registerPlugin(ScrollTrigger);

  const container = getContainer(data);
  if (!container) return;

  const wrapper = container.querySelector('.feature_sections');
  const sections = container.querySelectorAll('.feature_section');
  if (!wrapper || !sections.length) return;

  const fadeDur = 0.6;
  const fadeEase = 'cubic-bezier(0.32, 0.95, 0.6, 1)';
  const viewContents = (elm, dir) => {
    gsap.fromTo(elm, {
      opacity: 0,
      y: dir === 'up' ? 40 : -40,
      filter: 'blur(10px)',
    }, {
      opacity: 1,
      y: 0,
      filter: 'blur(0px)',
      duration: fadeDur,
      ease: fadeEase,
      overwrite: 'auto',
    });
  };
  const hideContents = (elm) => {
    gsap.to(elm, {
      opacity: 0,
      duration: fadeDur,
      overwrite: 'auto',
    });
  };

  sections.forEach((section) => {
    const meta = section.querySelector('.feature_section_meta');
    const body = section.querySelector('.feature_section_body');
    if (!meta || !body) return;

    // meta は CSS の position:fixed で viewport に固定済み。
    // JS は body の top/bottom が viewport を跨ぐタイミングで
    // 隣接する meta をクロスフェードさせる責務のみ。
    ScrollTrigger.create({
      trigger: body,
      start: 'top bottom',
      end: 'bottom center',
      invalidateOnRefresh: true,
      onEnter:     () => viewContents(meta, 'up'),
      onEnterBack: () => viewContents(meta, 'down'),
      onLeave:     () => hideContents(meta),
      onLeaveBack: () => hideContents(meta),
    });
  });

  // .feature_bottom は閉じの heading を CSS の position:fixed で viewport 中央に
  // 固定済み。top が viewport center を跨いだら .is-active で blur 解除を発火。
  const featureBottom = container.querySelector('.feature_bottom');
  if (featureBottom) {
    ScrollTrigger.create({
      trigger: featureBottom,
      start: 'top center',
      end: 'bottom top',
      invalidateOnRefresh: true,
      onEnter:     () => featureBottom.classList.add('is-active'),
      onEnterBack: () => featureBottom.classList.add('is-active'),
      onLeave:     () => featureBottom.classList.remove('is-active'),
      onLeaveBack: () => featureBottom.classList.remove('is-active'),
    });

    // feature_bottom が viewport を通過する範囲 (top=bottom → bottom=top) で
    // 背景 KV (.kv.is-feature .kv_figure_wrap) をわずかにスケールアップ
    // CSS 初期値 scale(1.2) → 1.32 を scrub
    const kvFigureWrap = container.querySelector('.kv.is-feature .kv_figure_wrap');
    if (kvFigureWrap) {
      gsap.fromTo(
        kvFigureWrap,
        { scale: 1.2 },
        {
          scale: 1.32,
          ease: 'none',
          scrollTrigger: {
            trigger: featureBottom,
            start: 'top bottom',
            end: 'bottom top',
            scrub: true,
            invalidateOnRefresh: true,
          },
        }
      );
    }
  }

  ScrollTrigger.refresh();
};
